import React from 'react';

import { Card, CardContent } from 'components/ui/card';
import type { AIUsageRow, AIUsageSummary as AIUsageSummaryData } from 'services/api/aiSettingsClient';

const numberFormat = new Intl.NumberFormat();
const compactFormat = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

const formatPercent = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);

const formatDuration = (value: number | null) => {
  if (value === null) return '—';
  return value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${value} ms`;
};

const changeLabel = (current: number, previous: number): string | null => {
  if (!previous) return null;
  const change = (current - previous) / previous;
  const arrow = change >= 0 ? '▲' : '▼';
  return `${arrow} ${Math.abs(change * 100).toFixed(0)}% vs previous 30 days`;
};

type KpiCardProps = { label: string; value: string; detail?: string | null; testId: string };

const KpiCard: React.FC<KpiCardProps> = ({ label, value, detail, testId }) => (
  <Card className="w-full max-w-none" maxWidth="none" disableAnimation data-testid={testId}>
    <CardContent className="items-stretch space-y-1 py-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-3xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="min-h-[1.25rem] text-xs text-muted-foreground">{detail ?? ''}</p>
    </CardContent>
  </Card>
);

export const AIUsageKpis: React.FC<{ usage: AIUsageSummaryData | null; isLoading: boolean }> = ({ usage, isLoading }) => {
  const current = usage?.current;
  const placeholder = isLoading ? '…' : '—';
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="AI usage, last 30 days">
      <KpiCard
        testId="ai-kpi-requests"
        label="AI requests · 30 days"
        value={current ? numberFormat.format(current.requests) : placeholder}
        detail={current && usage ? changeLabel(current.requests, usage.previous.requests) : null}
      />
      <KpiCard
        testId="ai-kpi-failure-rate"
        label="Failure rate"
        value={current ? formatPercent(current.error_rate) : placeholder}
        detail={current ? `${numberFormat.format(current.errors)} failed` : null}
      />
      <KpiCard
        testId="ai-kpi-tokens"
        label="Tokens · 30 days"
        value={current ? compactFormat.format(current.input_tokens + current.output_tokens) : placeholder}
        detail={current ? `${compactFormat.format(current.input_tokens)} in · ${compactFormat.format(current.output_tokens)} out` : null}
      />
      <KpiCard
        testId="ai-kpi-latency"
        label="Avg response time"
        value={current ? formatDuration(current.avg_duration_ms) : placeholder}
        detail={null}
      />
    </div>
  );
};

const UsageTable: React.FC<{ title: string; rows: AIUsageRow[]; nameHeader: string }> = ({ title, rows, nameHeader }) => (
  <Card className="w-full max-w-none" maxWidth="none" disableAnimation>
    <CardContent className="items-stretch space-y-3 py-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 pr-4 font-semibold">{nameHeader}</th>
                <th className="py-2 pr-4 text-right font-semibold">Requests</th>
                <th className="py-2 pr-4 text-right font-semibold">Failed</th>
                <th className="py-2 pr-4 text-right font-semibold">Tokens</th>
                <th className="py-2 text-right font-semibold">Avg time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className="border-t border-border">
                  <td className="py-2 pr-4 text-foreground">{row.label ?? row.name}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{numberFormat.format(row.requests)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{numberFormat.format(row.errors)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{compactFormat.format(row.total_tokens)}</td>
                  <td className="py-2 text-right tabular-nums">{formatDuration(row.avg_duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No requests in the last 30 days.</p>
      )}
    </CardContent>
  </Card>
);

export const AIUsageTables: React.FC<{ usage: AIUsageSummaryData | null; isLoading: boolean }> = ({ usage, isLoading }) => {
  if (!usage) {
    return <p className="text-sm text-muted-foreground">{isLoading ? 'Loading usage...' : 'Usage is unavailable.'}</p>;
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        OpenAI requests recorded by WiseFriend. ElevenLabs, Google speech, and image generation aren't included.
      </p>
      <div className="grid gap-4 xl:grid-cols-2">
        <UsageTable title="By feature" rows={usage.by_feature} nameHeader="Feature" />
        <UsageTable title="By model" rows={usage.by_model} nameHeader="Model" />
      </div>
    </div>
  );
};
